import { useEffect, useRef, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { ImagePlus, Trash2, ImageOff } from "lucide-react";
import { toast } from "sonner";

// Owner/Trainer approved reference photo galleries per classification option.
// View-only for technicians; Owner/Manager may upload/manage.
export const ReferenceGallery = ({ classificationType, options, canManage }) => {
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState("");
  const inputs = useRef({});

  const load = async () => {
    const { data } = await api.get("/reference-images", { params: { classification_type: classificationType } });
    setImages(data);
  };
  useEffect(() => { load(); }, [classificationType]);

  const upload = async (optionKey, title, file) => {
    if (!file) return;
    setBusy(optionKey);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/reference-images?classification_type=${classificationType}&option_key=${encodeURIComponent(optionKey)}&title=${encodeURIComponent(title)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Approved reference photo added");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setBusy(""); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this approved reference photo?")) return;
    try { await api.delete(`/reference-images/${id}`); toast.success("Reference photo removed"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  return (
    <div className="sg-card p-4 mt-2" data-testid={`ref-gallery-${classificationType}`}>
      <div className="font-head font-bold text-sm flex items-center gap-2">
        Owner / Trainer Approved Reference Images
      </div>
      <div className="text-xs mt-1 mb-3" style={{ color: "var(--txt2)" }}>
        Real-world examples for each level. {canManage ? "Upload only professionally approved photos." : "View-only — approved by Owner/Trainer."}
      </div>
      <div className="flex flex-col gap-4">
        {options.map(([key, label, tag, desc]) => {
          const shots = images.filter((im) => im.option_key === key);
          return (
            <div key={key} className="sg-card-elev p-3" data-testid={`ref-option-${classificationType}-${key}`}>
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{label} <span className="font-mono text-xs" style={{ color: "var(--txt2)" }}>· {tag}</span></div>
                {canManage && (
                  <>
                    <button className="sg-btn" style={{ minHeight: 34, padding: "4px 10px" }}
                      onClick={() => inputs.current[key]?.click()} data-testid={`ref-upload-${classificationType}-${key}`}>
                      <ImagePlus size={14} /> {busy === key ? "Uploading…" : "Add photo"}
                    </button>
                    <input ref={(el) => (inputs.current[key] = el)} type="file" accept="image/*" className="hidden"
                      onChange={(e) => { upload(key, label, e.target.files[0]); e.target.value = ""; }} />
                  </>
                )}
              </div>
              {desc && <div className="text-xs mt-1" style={{ color: "var(--txt2)" }}>Example: {desc}</div>}
              <div className="flex flex-wrap gap-2 mt-2">
                {shots.length === 0 ? (
                  <div className="flex items-center gap-2 text-xs" style={{ color: "var(--txt2)" }} data-testid={`ref-empty-${classificationType}-${key}`}>
                    <ImageOff size={14} /> No approved reference photos yet
                  </div>
                ) : shots.map((im) => (
                  <div key={im.id} className="relative" data-testid={`ref-image-${im.id}`}>
                    <img src={fileUrl(im.storage_path)} alt={im.title}
                      className="rounded-lg object-cover" style={{ width: 96, height: 96, border: "1px solid var(--brd)" }} />
                    <div className="text-[10px] mt-0.5 leading-tight" style={{ color: "var(--txt2)", maxWidth: 96 }}>
                      {im.uploaded_by_name} ({im.uploaded_by_role})
                    </div>
                    {canManage && (
                      <button onClick={() => remove(im.id)} data-testid={`ref-delete-${im.id}`}
                        className="absolute -top-2 -right-2 rounded-full p-1"
                        style={{ background: "var(--danger)", color: "#fff" }}><Trash2 size={12} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
